import React from 'react';
import { string, arrayOf, oneOf, bool, shape, func } from 'prop-types';
import ClassNames from 'classnames';
import * as ChartJS from 'chart.js';

import { isEqual, pick } from 'lodash';

import BaseComponent from '../../.internal/BaseComponent';

import './Chart.scss';
import defaultStyles from '../../../scss/variables/_exports.scss';

let chartObj;

/**
 * @deprecated
 */
export default class Chart extends BaseComponent {
  constructor(props) {
    super(props);
    this.canvasRef = React.createRef();
  }

  componentDidMount() {
    this.buildChart();
  }

  componentDidUpdate(prevProps) {
    const keys = [
      'title',
      'type',
      'displayTitle',
      'displayLegend',
      'legendPosition',
      'tooltips',
      'labels',
      'datasets'
    ];
    const oldProps = pick(prevProps, keys);
    const newProps = pick(this.props, keys);

    if (!isEqual(oldProps, newProps)) {
      this.buildChart();
    }
  }

  buildChart = () => {
    const {
      title,
      type,
      datasets,
      labels,
      tooltips,
      displayTitle,
      displayLegend,
      legendPosition
    } = this.props;

    const {
      formDatasets,
      handleClick,
      handleHover,
      handleLegendClick,
      handleLegendHover
    } = this;

    const chartConfig = {
      type,
      data: {
        labels,
        datasets: formDatasets(datasets)
      },
      options: {
        title: {
          display: displayTitle,
          text: title
        },
        legend: {
          display: displayLegend,
          position: legendPosition,
          labels: {
            fontSize: 14,
            fontFamily: defaultStyles.fontFamily
          },
          onClick: handleLegendClick,
          onHover: handleLegendHover
        },
        tooltips: tooltips.length
          ? {
              callbacks: {
                label: item => {
                  return `${tooltips[item.index]}: ${item.value}`;
                }
              }
            }
          : {},
        onClick: handleClick,
        onHover: handleHover,
        scales: {
          xAxes: [
            {
              ticks: {
                beginAtZero: true
              }
            }
          ],
          yAxes: [
            {
              ticks: {
                beginAtZero: true
              }
            }
          ]
        }
      }
    };

    const canvas = this.canvasRef.current;
    const ctx = canvas.getContext('2d');

    if (typeof chartObj !== 'undefined') chartObj.destroy();

    chartObj = new ChartJS(ctx, chartConfig);
  };

  formDatasets = datasets => {
    return datasets.map(dataset => {
      // set default colors if none is provided
      const backgroundColor =
        !!dataset.backgroundColor && dataset.backgroundColor.length
          ? dataset.backgroundColor
          : dataset.data.map(() => defaultStyles.brandColor);
      const borderColor =
        !!dataset.borderColor && dataset.borderColor.length
          ? dataset.borderColor
          : dataset.data.map(() => defaultStyles.brandColor);

      return {
        label: dataset.label || '',
        data: dataset.data || [],
        backgroundColor,
        borderColor: this.props.type === 'line' ? borderColor : '',
        borderWidth: 2,
        fill: false
      };
    });
  };

  handleClick = (e, item) => {
    const { onClick } = this.props;

    if (item.length && onClick) {
      onClick(e, item);
    }
  };

  handleHover = (e, item) => {
    const { onHover } = this.props;

    if (item.length && onHover) {
      onHover(e, item);
    }
  };

  handleLegendClick = (e, item) => {
    const { onLegendClick } = this.props;

    if (item && onLegendClick) {
      onLegendClick(e, item);
    }
  };

  handleLegendHover = (e, item) => {
    const { onLegendHover } = this.props;

    if (item && onLegendHover) {
      onLegendHover(e, item);
    }
  };

  render() {
    const { style, id, className } = this.props;

    const classNames = ClassNames('Chart', className);

    return (
      <div id={id} style={style} className={classNames}>
        <canvas
          ref={this.canvasRef}
          className="chart-canvas"
          data-chromatic="ignore"
        />
      </div>
    );
  }
}

Chart.propTypes = {
  title: string,
  type: oneOf(['line', 'bar', 'pie']),
  datasets: arrayOf(
    shape({
      backgroundColor: string,
      borderColor: string,
      data: arrayOf(shape({})),
      label: string
    })
  ),
  labels: arrayOf(string),
  tooltips: arrayOf(string),
  displayTitle: bool,
  displayLegend: bool,
  legendPosition: string,
  style: shape({}),
  id: string,
  className: string,
  onClick: func,
  onHover: func,
  onLegendClick: func,
  onLegendHover: func
};

Chart.defaultProps = {
  title: undefined,
  type: 'line',
  datasets: [],
  labels: [],
  tooltips: [],
  displayTitle: true,
  displayLegend: false,
  legendPosition: 'top',
  style: {},
  id: undefined,
  className: undefined
};
